import type { CheckoutConfig } from '../types';

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Partial<CheckoutConfig> = {
  environment: 'sandbox',
};

/**
 * Validate configuration object
 */
export function validateConfig(config: CheckoutConfig): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.merchantId) {
    errors.push('Merchant ID is required');
  }

  if (!config.productId) {
    errors.push('Product ID is required');
  }

  if (config.environment && !['sandbox', 'production'].includes(config.environment)) {
    errors.push('Environment must be either "sandbox" or "production"');
  }

  if (config.theme) {
    if (!['light', 'dark'].includes(config.theme.theme)) {
      errors.push('Theme must be either "light" or "dark"');
    }

    if (typeof config.theme.show_product_info !== 'boolean') {
      errors.push('show_product_info must be a boolean');
    }

    if (!['left', 'above'].includes(config.theme.product_layout)) {
      errors.push('product_layout must be either "left" or "above"');
    }

    if (typeof config.theme.show_coupon_row !== 'boolean') {
      errors.push('show_coupon_row must be a boolean');
    }

    if (
      config.theme.coupon_row_disclaimer !== undefined &&
      typeof config.theme.coupon_row_disclaimer !== 'string'
    ) {
      errors.push('coupon_row_disclaimer must be a string');
    }

    if (typeof config.theme.accent_color !== 'string') {
      errors.push('accent_color must be a string');
    }

    // Validate hex color format
    if (!/^#[0-9A-F]{6}$/i.test(config.theme.accent_color)) {
      errors.push('accent_color must be a valid hex color (e.g., #007bff)');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Merge configuration with defaults
 */
export function mergeConfig(config: Partial<CheckoutConfig>): CheckoutConfig {
  const merged = { ...DEFAULT_CONFIG, ...config };

  // Ensure required fields are present
  if (!merged.merchantId) {
    throw new Error('Merchant ID is required');
  }

  if (!merged.productId) {
    throw new Error('Product ID is required');
  }

  return merged as CheckoutConfig;
}

/**
 * Create a minimal configuration object
 */
export function createMinimalConfig(merchantId: string, productId: string, checkoutSessionSecret: string, environment?: 'sandbox' | 'production'): CheckoutConfig {
  return {
    merchantId,
    productId,
    checkoutSessionSecret,
    environment: environment || 'sandbox'
  };
}

/**
 * Check if configuration is complete
 */
export function isConfigComplete(config: Partial<CheckoutConfig>): config is CheckoutConfig {
  return !!(config.merchantId && config.productId && config.checkoutSessionSecret);
}

/**
 * Get configuration summary for debugging
 */
export function getConfigSummary(config: CheckoutConfig): Record<string, any> {
  return {
    merchantId: config.merchantId,
    productId: config.productId,
    environment: config.environment,
    hasTheme: !!config.theme,
    themeDetails: config.theme ? {
      theme: config.theme.theme,
      showProductInfo: config.theme.show_product_info,
      productLayout: config.theme.product_layout,
      showCouponRow: config.theme.show_coupon_row,
      couponRowDisclaimer: config.theme.coupon_row_disclaimer,
      accentColor: config.theme.accent_color
    } : undefined
  };
} 